import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookThumb } from '@/components/insights'
import { Button, Card, CardContent, Select } from '@/components/ui'
import { useLibraryBooks } from '@/hooks/insights'
import { yearInReview, yearsWithReads } from '@/lib/insights'
import { renderShareImage } from '@/lib/insights/shareImage'
import { captureError } from '@/lib/monitoring'

export function YearInReviewPage() {
  const { t, i18n } = useTranslation()
  const { data: books = [], isLoading } = useLibraryBooks()
  const currentYear = new Date().getFullYear()

  const years = useMemo(() => {
    const withReads = yearsWithReads(books)
    return withReads.includes(currentYear) ? withReads : [currentYear, ...withReads]
  }, [books, currentYear])
  const [year, setYear] = useState(currentYear)

  const review = useMemo(() => yearInReview(books, year), [books, year])
  const monthName = (month: number) =>
    new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(new Date(year, month - 1, 1))

  const [shareState, setShareState] = useState<'idle' | 'working' | 'downloaded'>('idle')

  const shareText = `${t('insights.yearInReview.shareTitle', { year })} — longbourn.club`

  const buildImage = () =>
    renderShareImage({
      year,
      title: t('insights.yearInReview.imageTitle'),
      booksRead: review.booksRead.length,
      booksReadLabel: t('insights.yearInReview.imageBooksRead', {
        count: review.booksRead.length,
      }),
      highlights: [
        review.topGenre && {
          label: t('insights.yearInReview.topGenre'),
          value: review.topGenre.genre.name,
          detail: t('insights.bookCount', { count: review.topGenre.count }),
        },
        review.topAuthor && {
          label: t('insights.yearInReview.topAuthor'),
          value: review.topAuthor.author,
          detail: t('insights.bookCount', { count: review.topAuthor.count }),
        },
        review.bestRated && {
          label: t('insights.yearInReview.bestRated'),
          value: review.bestRated.title,
          detail: '★'.repeat(review.bestRated.rating ?? 0),
        },
        review.busiestMonth && {
          label: t('insights.yearInReview.busiestMonth'),
          value: capitalize(monthName(review.busiestMonth.month)),
          detail: t('insights.bookCount', { count: review.busiestMonth.count }),
        },
        review.fastest && {
          label: t('insights.yearInReview.fastest'),
          value: review.fastest.book.title,
          detail: t('insights.days', { count: review.fastest.days }),
        },
      ].filter((h): h is { label: string; value: string; detail: string } => !!h),
      footerStats: [
        review.averageRating !== null &&
          `${t('insights.yearInReview.averageRating')} ${review.averageRating}★`,
        review.averageDays !== null &&
          `${t('insights.days', { count: review.averageDays })} ${t('insights.readingTime.perBook')}`,
        `${t('books.type.physical')} ${review.physical} · ${t('books.type.digital')} ${review.digital}`,
      ].filter((s): s is string => !!s),
      brand: 'Longbourn',
      url: 'longbourn.club',
    })

  // Mobile: native share sheet with the image. Desktop (no file sharing): download the PNG.
  const handleShare = async () => {
    setShareState('working')
    try {
      const blob = await buildImage()
      const file = new File([blob], `longbourn-${year}.png`, { type: 'image/png' })

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText })
        setShareState('idle')
        return
      }

      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = file.name
      link.click()
      URL.revokeObjectURL(url)
      setShareState('downloaded')
      setTimeout(() => setShareState('idle'), 2500)
    } catch (error) {
      // AbortError = user closed the share sheet
      if ((error as Error).name !== 'AbortError') {
        captureError(error as Error, { context: 'Failed to share year in review image' })
      }
      setShareState('idle')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {t('common.loading')}
      </div>
    )
  }

  const highlights = [
    {
      icon: '🏷️',
      label: t('insights.yearInReview.topGenre'),
      value: review.topGenre?.genre.name,
      detail: review.topGenre && t('insights.bookCount', { count: review.topGenre.count }),
    },
    {
      icon: '✍️',
      label: t('insights.yearInReview.topAuthor'),
      value: review.topAuthor?.author,
      detail: review.topAuthor && t('insights.bookCount', { count: review.topAuthor.count }),
    },
    {
      icon: '⭐',
      label: t('insights.yearInReview.bestRated'),
      value: review.bestRated?.title,
      detail: review.bestRated && '⭐'.repeat(review.bestRated.rating ?? 0),
    },
    {
      icon: '⚡',
      label: t('insights.yearInReview.fastest'),
      value: review.fastest?.book.title,
      detail: review.fastest && t('insights.days', { count: review.fastest.days }),
    },
    {
      icon: '📅',
      label: t('insights.yearInReview.busiestMonth'),
      value: review.busiestMonth && monthName(review.busiestMonth.month),
      detail: review.busiestMonth && t('insights.bookCount', { count: review.busiestMonth.count }),
    },
    {
      icon: '⏱️',
      label: t('insights.yearInReview.averageTime'),
      value: review.averageDays !== null ? t('insights.days', { count: review.averageDays }) : null,
      detail: t('insights.readingTime.perBook'),
    },
  ].filter((h) => h.value)

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-foreground">
            🎉 {t('insights.yearInReview.title', { year })}
          </h1>
          <p className="mt-2 text-muted-foreground">{t('insights.yearInReview.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Select
            aria-label={t('insights.yearInReview.selectYear')}
            value={String(year)}
            onChange={(y) => setYear(Number(y))}
            className="w-28"
            options={years.map((y) => ({ value: String(y), label: String(y) }))}
          />
          <Button
            onClick={handleShare}
            disabled={review.booksRead.length === 0 || shareState === 'working'}
          >
            {shareState === 'working'
              ? t('insights.yearInReview.generating')
              : shareState === 'downloaded'
                ? t('insights.yearInReview.downloaded')
                : t('insights.yearInReview.share')}
          </Button>
        </div>
      </div>

      {review.booksRead.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12 text-center text-muted-foreground">
            <div className="mb-4 text-4xl">📚</div>
            <p>{t('insights.yearInReview.empty', { year })}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Headline numbers */}
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {[
              { label: t('insights.yearInReview.booksRead'), value: review.booksRead.length },
              { label: t('insights.yearInReview.booksAdded'), value: review.booksAdded },
              {
                label: t('insights.yearInReview.averageRating'),
                value: review.averageRating !== null ? `${review.averageRating}⭐` : '-',
              },
              {
                label: t('insights.yearInReview.physicalDigital'),
                value: `${review.physical} / ${review.digital}`,
              },
            ].map((stat) => (
              <Card key={stat.label} className="border-0 shadow-sm">
                <CardContent className="p-5">
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="mt-1 text-3xl font-bold text-foreground">{stat.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {highlights.map((h) => (
                <Card key={h.label} className="border-0 shadow-sm min-w-0">
                  <CardContent className="p-5">
                    <p className="text-sm text-muted-foreground">
                      <span className="mr-2" aria-hidden="true">
                        {h.icon}
                      </span>
                      {h.label}
                    </p>
                    <p className="mt-1 truncate text-lg font-semibold text-foreground">{h.value}</p>
                    {h.detail && <p className="text-xs text-muted-foreground">{h.detail}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Every book of the year, in reading order */}
          <div>
            <h2 className="mb-4 text-xl font-semibold text-foreground">
              {t('insights.yearInReview.allBooks')}
            </h2>
            <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {review.booksRead.map((book) => (
                <li
                  key={book.id}
                  className="flex min-w-0 items-center gap-3 rounded-lg bg-card p-3 shadow-sm"
                >
                  <BookThumb src={book.cover_url} title={book.title} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{book.title}</p>
                    <p className="truncate text-xs text-muted-foreground">{book.author}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Intl.DateTimeFormat(i18n.language, {
                        day: 'numeric',
                        month: 'short',
                      }).format(new Date(`${book.end_date}T00:00:00`))}
                      {book.rating ? ` · ${'⭐'.repeat(book.rating)}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  )
}

function capitalize(text: string) {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1)
}
